import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message } from "antd";
import { createPosition, createPositionAlias, getAllPositions, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const PositionComponent = ({ exception, onClose, onResolved }) => {
    const [positionName, setPositionName] = useState(null);
    const [positionDescription, setPositionDescription] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [positions, setPositions] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const extractPositionDetails = () => {
            setPositionName(exception?.unknown_infomation);
            setPositionDescription(exception?.description || exception?.unknown_infomation || '');
        };

        if (exception && exception.description) {
            extractPositionDetails();
        }

        const fetchPositions = async () => {
            setLoading(true);
            try {
                const response = await getAllPositions();
                const positionsData = response?.data?.data || [];
                setPositions(Array.isArray(positionsData) ? positionsData : []);
            } catch (error) {
                message.error('Failed to fetch positions');
            } finally {
                setLoading(false);
            }
        };

        fetchPositions();
    }, [exception]);

    const handleMarkAsNew = async () => {
        setLoading(true);
        if (positionName) {
            if (exception.status === 'pending') {
                try {
                    const payload = {
                        id: 0,
                        name: positionName,
                        description: positionDescription || positionName,
                        alias_for: 0,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    };
                    
                    await createPosition(payload);
                    await updateException(exception.id);
                    message.success('Position created');
                    try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                    onClose();
                } catch (error) {
                    message.error('Failed to create position');
                } finally {
                    setLoading(false);
                }
            } else {
                console.warn("Exception already resolved.");
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            console.warn("Position name not found.");
            setLoading(false);
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handlePositionSelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        setLoading(true);
        if (exception.status === 'pending') {
            try {
                const payload = {
                    name: positionName,
                    description: positionDescription || positionName,
                    alias_for: selectedAliasId
                };
                
                await createPositionAlias(payload);
                await updateException(exception.id);
                message.success('Alias saved');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } catch (error) {
                message.error('Failed to save alias');
            } finally {
                setLoading(false);
            }
        } else {
            console.warn("Exception already resolved.");
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    {positionName ? `Select an existing position to alias "${positionName}" to.` : 'Select an existing position to alias.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a position"
                                    onChange={handlePositionSelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {positions.map((position) => (
                                        <Option key={position.id} value={position.id}>
                                            {position.name} - {position.description}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default PositionComponent;
